import React, { useState, useEffect, useRef } from 'react';
import './WorkWallH.css';
import { WORKS, catLabel } from './worksData';
import WorkVisual from './WorkVisual';
import WorkIndex2 from './WorkIndex2';
import { loadDisplayWorks } from './store';

const ROWS = 3;

// Distance à laquelle le sélecteur réagit : la case part vers le bouton visé
// avant que le curseur ne l'atteigne, pour qu'on voie le déplacement.
// (Le débordement de la couche goo est défini en CSS : .wallh-goo-shapes)
const GOO_ZONE = 95;

// Largeurs variées des cases (en vw) pour un défilé vivant
// (les largeurs ne sont plus imposées : chaque case prend le format
//  naturel de son visuel, cf. .wallh-cell dans la feuille de style)

export default function WorkWallH({ onOpenWork }) {
  const [items, setItems] = useState(WORKS);
  const [mode, setMode] = useState('mur'); // 'mur' | 'liste'
  const [modeLeaving, setModeLeaving] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  const [hoverMode, setHoverMode] = useState(null); // bouton survolé du sélecteur
  const wallRef = useRef(null);
  const trackRefs = useRef([]);
  const rowState = useRef([]); // { pos, hovered } par rangée
  const toggleRef = useRef(null);
  const murBtnRef = useRef(null);
  const listeBtnRef = useRef(null);
  const gooPillRef = useRef(null);
  const gooDotRef = useRef(null);
  const gooShapesRef = useRef(null);
  const hoverModeRef = useRef(null); // évite de re-rendre à chaque mousemove

  // Bouton sous la case bleue : celui survolé, sinon le mode réellement actif
  const litMode = hoverMode || mode;

  // Téléphone : mise en page simple (travaux empilés, scrollables)
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Téléphone : le logo et le bouton MENU restent bleus tant que rien ne
  // passe derrière eux, et basculent en mode différence dès qu'un travail
  // arrive sous la barre de navigation pendant le défilement.
  useEffect(() => {
    if (!isMobile) return undefined;
    const scroller = document.querySelector('.content-module');

    const update = () => {
      const header = document.querySelector('.header');
      const navBas = header ? header.getBoundingClientRect().bottom : 70;
      const cells = document.querySelectorAll('.wallh-mcell');
      let derriere = false;
      for (let i = 0; i < cells.length; i++) {
        const r = cells[i].getBoundingClientRect();
        if (r.top < navBas && r.bottom > 0) { derriere = true; break; }
      }
      document.body.classList.toggle('work-under-nav', derriere);
    };

    update();
    if (scroller) scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      if (scroller) scroller.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      document.body.classList.remove('work-under-nav');
    };
  }, [isMobile, items]);

  // Défilé piloté en JS : avance automatique, pause au survol,
  // et contrôle à la molette de la rangée survolée.
  useEffect(() => {
    if (mode !== 'mur' || isMobile) return undefined;

    let raf;
    let last = performance.now();
    const DURATION = (i) => 75 + i * 15; // secondes pour un tour complet

    const step = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      trackRefs.current.forEach((el, i) => {
        if (!el) return;
        let st = rowState.current[i];
        if (!st) st = rowState.current[i] = { pos: 0, hovered: false };
        // période de la boucle : une copie + l'espace qui la relie à la suivante
        const half = (el.scrollWidth + 6) / 2;
        if (!half || el.scrollWidth === 0) return;
        const dir = i % 2 === 1 ? -1 : 1; // directions alternées
        if (!st.hovered) st.pos += (half / DURATION(i)) * dir * dt;
        // boucle sans couture : la position reste dans [0, half)
        st.pos = ((st.pos % half) + half) % half;
        el.style.transform = `translateX(${-st.pos}px)`;
      });
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    // Molette : fait tourner manuellement la rangée survolée
    const wall = wallRef.current;
    const onWheel = (e) => {
      const idx = rowState.current.findIndex((s) => s && s.hovered);
      if (idx === -1) return;
      e.preventDefault();
      rowState.current[idx].pos += e.deltaY * 0.9;
    };
    if (wall) wall.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      cancelAnimationFrame(raf);
      if (wall) wall.removeEventListener('wheel', onWheel);
    };
  }, [mode, items, isMobile]);

  const setRowHover = (i, value) => {
    if (!rowState.current[i]) rowState.current[i] = { pos: 0, hovered: false };
    rowState.current[i].hovered = value;
  };

  // La case bleue se cale sur le bouton concerné (glissement via transition CSS)
  useEffect(() => {
    if (isMobile) return;
    const btn = litMode === 'liste' ? listeBtnRef.current : murBtnRef.current;
    const pill = gooPillRef.current;
    const shapes = gooShapesRef.current;
    if (!btn || !pill || !shapes) return;
    // Repère = la couche goo elle-même : évite tout écart dû à la bordure
    // du sélecteur (l'absolu se cale sur l'intérieur de celle-ci).
    const rb = btn.getBoundingClientRect();
    const rs = shapes.getBoundingClientRect();
    pill.style.left = rb.left - rs.left + 'px';
    pill.style.top = rb.top - rs.top + 'px';
    pill.style.width = rb.width + 'px';
    pill.style.height = rb.height + 'px';
  }, [litMode, isMobile, mode]);

  // Fusion du curseur avec la case bleue : en approchant, la goutte rétrécit
  // et se fond dedans ; en sortant, tout revient à l'état initial.
  useEffect(() => {
    if (isMobile) return undefined;

    const onMove = (e) => {
      const wrap = toggleRef.current;
      const dot = gooDotRef.current;
      const pill = gooPillRef.current;
      if (!wrap || !dot || !pill) return;

      const r = wrap.getBoundingClientRect();
      const dedans =
        e.clientX > r.left - GOO_ZONE && e.clientX < r.right + GOO_ZONE &&
        e.clientY > r.top - GOO_ZONE && e.clientY < r.bottom + GOO_ZONE;

      if (!dedans) {
        dot.style.visibility = 'hidden';
        document.body.classList.remove('wallh-goo-active');
        if (hoverModeRef.current !== null) {
          hoverModeRef.current = null;
          setHoverMode(null); // la case revient sur le mode actif
        }
        return;
      }

      // Anticipation : dès qu'on approche, la case part vers le bouton le
      // plus proche — elle est donc déjà en place quand le curseur arrive.
      const mur = murBtnRef.current;
      const liste = listeBtnRef.current;
      if (mur && liste) {
        const rm = mur.getBoundingClientRect();
        const rl = liste.getBoundingClientRect();
        const dM = Math.hypot(e.clientX - (rm.left + rm.width / 2), e.clientY - (rm.top + rm.height / 2));
        const dL = Math.hypot(e.clientX - (rl.left + rl.width / 2), e.clientY - (rl.top + rl.height / 2));
        const vise = dL < dM ? 'liste' : 'mur';
        if (hoverModeRef.current !== vise) {
          hoverModeRef.current = vise;
          setHoverMode(vise);
        }
      }

      // Position de la goutte dans le repère de la couche goo.
      // +15 : la goutte du site est un cercle de 60px posé à (pointeur - 15),
      // son centre visuel est donc à (pointeur + 15). Sans ce décalage, la
      // forme goo ne coïncide pas avec elle et on voit un saut au moment
      // où l'une remplace l'autre.
      const shapes = gooShapesRef.current;
      if (!shapes) return;
      const rs = shapes.getBoundingClientRect();
      const x = e.clientX - rs.left + 15;
      const y = e.clientY - rs.top + 15;

      // Fonte progressive : la goutte ne rétrécit qu'au CONTACT de la case,
      // pas avant. On mesure la distance à son bord (et non à son centre) :
      // tant qu'on est à l'extérieur, la goutte garde sa taille et vient
      // toucher le carré ; la fonte se fait pendant le chevauchement.
      // Référence = le SÉLECTEUR entier, pas seulement la case bleue.
      // Une fois la goutte absorbée, elle le reste tant qu'on circule à
      // l'intérieur : en se référant à la case (qui met 0,35 s à glisser),
      // la goutte ressortait au milieu du trajet avant de refondre.
      const pl = r.left - rs.left;
      const pt = r.top - rs.top;
      const pw = r.width;
      const ph = r.height;
      // distance signée au bord du rectangle (négative une fois à l'intérieur)
      const dx = Math.max(pl - x, 0, x - (pl + pw));
      const dy = Math.max(pt - y, 0, y - (pt + ph));
      const horsCase = Math.hypot(dx, dy);
      const dedansX = Math.min(x - pl, pl + pw - x);
      const dedansY = Math.min(y - pt, pt + ph - y);
      const profondeur = horsCase > 0 ? -horsCase : Math.min(dedansX, dedansY);
      // profondeur : -30px (goutte intacte) → +13px (totalement absorbée).
      // 13 et non 18 : la goutte est centrée 15px sous le pointeur, elle
      // dépasse donc par le bas d'un sélecteur haut de 60px — la profondeur
      // réellement atteignable au centre d'un bouton plafonne vers 15px.
      const debut = -30;
      const fin = 13;
      let melt = 1 - (profondeur - debut) / (fin - debut);
      melt = Math.max(0.001, Math.min(1, melt));

      dot.style.visibility = 'visible';
      dot.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%) scale(${melt})`;
      // la goutte du site est remplacée par la forme goo
      document.body.classList.add('wallh-goo-active');
    };

    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      document.body.classList.remove('wallh-goo-active');
    };
  }, [isMobile]);

  // Bascule entre les modes avec animation de sortie puis d'entrée
  const switchMode = (next) => {
    if (next === mode || modeLeaving) return;
    setModeLeaving(true);
    setTimeout(() => {
      setMode(next);
      setModeLeaving(false);
    }, 350);
  };

  useEffect(() => {
    loadDisplayWorks().then((liste) => {
      if (liste.length > 0) setItems(liste);
    });
  }, []);

  // Répartition en rangées (round-robin)
  const rows = Array.from({ length: ROWS }, () => []);
  items.forEach((item, idx) => {
    rows[idx % ROWS].push({ ...item, idx });
  });

  // Téléphone : les travaux l'un au-dessus de l'autre, on scrolle simplement
  if (isMobile) {
    return (
      <div className="work-wallh-mobile">
        {items.map((item, idx) => (
          <a
            key={idx}
            href={item.link}
            className="wallh-mcell"
            onClick={(e) => {
              e.preventDefault();
              if (onOpenWork) onOpenWork(item);
            }}
          >
            <WorkVisual src={item.image} alt={item.label} className="wallh-visual" />
            <span className="wallh-overlay wallh-overlay--visible">
              <span className="wallh-title">{item.label}</span>
              <span className="wallh-cat">{item.category || catLabel(item.cat)}</span>
            </span>
          </a>
        ))}
      </div>
    );
  }

  return (
    <>
      {mode === 'liste' ? (
        <div className={`wallh-liste-wrap${modeLeaving ? ' wallh-mode-leaving' : ''}`}>
          <WorkIndex2 onOpenWork={onOpenWork} />
        </div>
      ) : (
        <div ref={wallRef} className={`work-wallh${modeLeaving ? ' wallh-mode-leaving' : ''}`}>
          <div className="work-wallh-inner">
        {rows.map((row, rowIdx) => (
          <div
            key={rowIdx}
            className="wallh-row"
            style={{ '--row': rowIdx }}
            onMouseEnter={() => setRowHover(rowIdx, true)}
            onMouseLeave={() => setRowHover(rowIdx, false)}
          >
            <div
              ref={(el) => { trackRefs.current[rowIdx] = el; }}
              className="wallh-track"
            >
              {/* Contenu doublé pour la boucle infinie sans couture */}
              {[0, 1].map((copy) => (
                <React.Fragment key={copy}>
                  {row.map((item, i) => (
                    <a
                      key={`${copy}-${item.idx}`}
                      href={item.link}
                      className="wallh-cell"
                      onClick={(e) => {
                        e.preventDefault();
                        if (onOpenWork) onOpenWork(item);
                      }}
                    >
                      <WorkVisual src={item.image} alt={item.label} className="wallh-visual" />
                      <span className="wallh-overlay">
                        <span className="wallh-title">{item.label}</span>
                        <span className="wallh-cat">{item.category || catLabel(item.cat)}</span>
                      </span>
                    </a>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}
          </div>
        </div>
      )}

      {/* Sélecteur de disposition : la case bleue glisse vers le bouton
          survolé et fusionne avec le curseur (effet goo) */}
      <div className="wallh-mode-toggle" ref={toggleRef}>
        <div className="wallh-goo-shapes" ref={gooShapesRef} aria-hidden="true">
          <svg width="0" height="0" style={{ position: 'absolute' }}>
            <defs>
              <filter id="wallh-goo-filter">
                <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur" />
                <feColorMatrix
                  in="blur"
                  mode="matrix"
                  values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 26 -12"
                />
              </filter>
            </defs>
          </svg>
          <div className="wallh-goo-inner">
            <span ref={gooPillRef} className="wallh-goo-pill" />
            <span ref={gooDotRef} className="wallh-goo-dot" />
          </div>
        </div>

        <button
          ref={murBtnRef}
          className={litMode === 'mur' ? 'lit' : ''}
          onClick={() => switchMode('mur')}
          aria-label="Vue mur"
          title="Mur"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <rect x="2" y="2" width="9.5" height="9.5" rx="2" />
            <rect x="13.5" y="2" width="8.5" height="9.5" rx="2" />
            <rect x="2" y="13.5" width="8.5" height="8.5" rx="2" />
            <rect x="12.5" y="13.5" width="9.5" height="8.5" rx="2" />
          </svg>
        </button>
        <button
          ref={listeBtnRef}
          className={litMode === 'liste' ? 'lit' : ''}
          onClick={() => switchMode('liste')}
          aria-label="Vue liste"
          title="Liste"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <rect x="2" y="3" width="4" height="4" rx="1.2" />
            <rect x="8.5" y="3.5" width="13.5" height="3" rx="1.5" />
            <rect x="2" y="10" width="4" height="4" rx="1.2" />
            <rect x="8.5" y="10.5" width="13.5" height="3" rx="1.5" />
            <rect x="2" y="17" width="4" height="4" rx="1.2" />
            <rect x="8.5" y="17.5" width="13.5" height="3" rx="1.5" />
          </svg>
        </button>
      </div>
    </>
  );
}
